import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Car, X, AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const POLL_MS = 60000;
const SEV = {
  red: { c: "#DC2626", bg: "#FEE2E2" },
  yellow: { c: "#B45309", bg: "#FEF3C7" },
  green: { c: "#15803D", bg: "#DCFCE7" },
};

export default function VeicoliBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const allowed = ["admin", "operatore"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ count: 0, items: [] });
  const wrapperRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/veicoli/scadenze", { params: { days: 30 } });
      setData(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!allowed) return;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [allowed, load]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!allowed) return null;

  const count = data.count || 0;
  const hasUrgent = (data.items || []).some((i) => i.days_left <= 7);
  const go = (id) => { setOpen(false); navigate(`/veicoli?open=${id}`); };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-black/5 transition"
        data-testid="veicoli-bell"
        aria-label="Scadenze veicoli"
        title={count > 0 ? `${count} scadenze veicoli nei prossimi 30 giorni` : "Nessuna scadenza veicoli"}
      >
        <Car className={`w-5 h-5 ${hasUrgent ? "text-red-600" : ""}`} />
        {count > 0 && (
          <span
            data-testid="veicoli-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white"
            style={{ background: hasUrgent ? "#DC2626" : "#F59E0B", padding: "0 5px" }}
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-h-[480px] bg-white rounded-xl shadow-2xl border z-50 flex flex-col"
          style={{ borderColor: "var(--border)" }}
          data-testid="veicoli-bell-dropdown"
        >
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <div>
              <div className="font-bold text-sm" style={{ fontFamily: "Manrope" }}>Scadenze veicoli</div>
              <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Prossimi 30 giorni · Assicurazione e Bollo</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-black/5" data-testid="veicoli-bell-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {count === 0 ? (
              <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>
                <Car className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Nessuna scadenza nei prossimi 30 giorni.
              </div>
            ) : (
              (data.items || []).map((it, i) => {
                const s = SEV[it.severity] || SEV.green;
                return (
                  <button
                    key={`${it.veicolo_id}-${it.field}-${i}`}
                    onClick={() => go(it.veicolo_id)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 hover:bg-black/[0.02] text-left"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`veicoli-bell-item-${it.veicolo_id}`}
                  >
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md shrink-0" style={{ background: s.bg, color: s.c }}>
                      <AlertTriangle className="w-3 h-3" />
                      {it.days_left < 0 ? "Scaduta" : `${it.days_left}gg`}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{it.marca_modello || "Veicolo"} · <span className="font-mono">{it.targa || "—"}</span></div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>{it.label} · scad. {it.scadenza}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 shrink-0" style={{ color: "var(--text-muted)" }} />
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
